<template>
  <b-container fluid class="chromatography-report-print">
    <div class="chromatography-report-print__title">
      <strong>
        {{
          $t('engineering.chromatography.methods-and-diagnostics.individual-analysis.print.title')
        }}
      </strong>
    </div>
    <s-loader v-if="loading" class="mt-2" size="lg" />
    <div v-else>
      <div class="chromatography-report-print__header">
        <div class="chromatography-report-print__sample-infos">
          <span class="chromatography-report-print__sample-number">
            <strong>{{ $t('engineering.reports.sample') }}:</strong>
            <span>{{ sample.number }}</span>
            <span v-show="sample.pendingSample">
              - {{ $t('general.attention') }}:
              {{ $t('engineering.reports.processing-pending-sample') }}
            </span>
          </span>
          <span
            class="chromatography-report-print__sample-date"
          >{{ sampleDateTime | dateTimeFilter({ timeZone: timezones.UTC }) }}</span>
        </div>
        <summary-transformer-info :loading.sync="loadingSummary" />
      </div>
      <alarme-level-parameters-table
        :assetModuleId="assetModuleId"
        :sampleId="sampleId"
        :loading.sync="loadingAlarmLevels"
      ></alarme-level-parameters-table>
      <div
        v-for="(ruleData, index) in sample.summary"
        :key="ruleData.rule"
        class="chromatography-report-print__avoid-break"
      >
        <rule-panel
          v-if="shouldShowRule(ruleData)"
          :rule="ruleData.rule"
          :methods="rulesData[index]"
          :show-only-alarmed-methods="showOnlyAlarmedMethods"
        />
      </div>
    </div>
  </b-container>
</template>

<script>
import SummaryTransformerInfo from '@/domain/components/installation/supervision/SummaryTransformerInfo';
import LoadingMixin from '@/services/loadingMixin';
import IndividualAnalysisService
  from '@/domain/services/installation/engineering/chromatography/individualAnalysis';
import sampleService
  from '@/domain/services/installation/engineering/chromatography/sample';
import print from '@/services/print';
import AlarmeLevelParametersTable from '@/domain/components/installation/engineering/chromatography/methodsAndDiagnostics/individualAnalysis/print/AlarmeLevelParametersTable';
import RulePanel from '@/domain/components/installation/engineering/chromatography/methodsAndDiagnostics/individualAnalysis/print/RulePanel';
import { timezones } from '@/constants/localizer';

export default {
  props: {
    sampleId: String,
    assetModuleId: String,
  },
  name: 'ChromatographyReportPrint',
  components: {
    SummaryTransformerInfo,
    AlarmeLevelParametersTable,
    RulePanel,
  },
  mixins: [LoadingMixin],
  data: () => ({
    timezones,
    loadingSummary: false,
    loadingAlarmLevels: false,
    sample: null,
    rulesData: [],
  }),
  computed: {
    sampleDateTime() {
      return this.sample?.sampleDateTime;
    },
    showOnlyAlarmedMethods() {
      return this.$route.query.alarmedOnly === 'true';
    },
  },
  methods: {
    async loadSample() {
      this.startLoading();
      this.sample = null;
      try {
        this.sample = await IndividualAnalysisService.getSampleSummary(
          this.assetModuleId,
          this.sampleId,
        );
        sampleService.get(this.assetModuleId, this.sampleId)
          .then((response) => {
            this.sample = {
              ...this.sample,
              ...response,
            };
          });
        const reportRulesPromises = this.sample.summary
          .map(({ rule }) => IndividualAnalysisService
            .getRuleResult(this.assetModuleId, this.sampleId, rule));
        this.rulesData = await Promise.all(reportRulesPromises);
      } finally {
        this.endLoading();
      }
    },
    callPrint() {
      if (!this.loading && !this.loadingSummary && !this.loadingAlarmLevels) {
        setTimeout(() => {
          print();
        }, 1000);
      }
    },
    shouldShowRule(ruleData) {
      if (this.showOnlyAlarmedMethods) {
        return ruleData?.alarmed;
      }

      return true;
    },
  },
  created() {
    this.loadSample();
  },
  watch: {
    loadingSummary() {
      this.callPrint();
    },
    loadingAlarmLevels() {
      this.callPrint();
    },
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"

  .chromatography-report-print
    padding: 0 25px
    &__avoid-break
      page-break-inside: avoid
    &__header
      margin-bottom: 20px
    &__title
      font-size: 1.2rem
      padding: 10px
      text-align: center
    &__sample-number
      font-size: 1.2rem
    &__sample-infos
      display: flex
      justify-content: space-between
  @media print
    .chromatography-report-print
      font-family: "Trebuchet MS", Helvetica, sans-serif
      font-size: .94rem
      max-height: 100%
      max-width: 100%
      ::v-deep
        table.table thead th
          background-color: var(--color-table-header-bg) !important
        tr td div
          page-break-inside: avoid
        .summary-transformer-info
          padding: 5px 10px 2px
          font-size: .94rem
  @page
    margin: 8mm 0
    size: A4 !important
</style>
